






















import { Component, Prop, Vue, Watch } from 'vue-property-decorator';
import LineChart from '@/components/charts/LineChart.vue';
import { getParcelCycle, getParcelStatus, parcelMatchFilters } from '@/services/analyticData';
import { DashboardAnalyticData } from '@/interfaces/dashboardAnalyticData';
import { stringToMomentDate } from '@/services/date';
import Constants from '@/services/constants';
import moment from 'moment';
import { Parcel } from '@/interfaces/parcel';
import { CHART_TYPE, getColor } from '@/services/charts';
import { ProductType } from '@/enums/productType';
import { ProductGroupName } from '@/enums/productGroupName';

@Component({
  components: {
    LineChart
  }
})
export default class AnalyticsLineChartPerDate extends Vue {
  productGroupName = ProductGroupName;
  @Prop() analytics: DashboardAnalyticData[];
  @Prop() label: string;
  @Prop() hideNumberOfCycles: boolean;

  selectedType: string = CHART_TYPE.VARIETY;
  CHART_TYPE = CHART_TYPE;

  chartData = null;
  chartOptions = null;

  mounted(): void {
    this.setupChartData();
  }

  @Watch('analytics')
  onAnalyticDataChanged(): void {
    this.$nextTick(() => this.setupChartData());
  }

  deferSetupChartData(): void {
    setTimeout(() => this.setupChartData(), 0);
  }

  setupChartData(): void {
    const isVegetativeIndex = this.$store.state.analytic.selectedProductType === ProductType.VEGETATIVE_INDEX;

    if (isVegetativeIndex) {
      this.setupCountBasedChart();
    } else {
      this.setupRegularChart();
    }
  }

  private setupRegularChart(): void {
    this.chartOptions = {
      maintainAspectRatio: false,
      legend: {
        display: true,
        position: 'bottom',
        labels: {
          padding: 12,
          usePointStyle: true,
          fontSize: 14,
          fontColor: '#161C26',
          fontFamily: 'Inter'
        }
      },
      tooltips: {
        enabled: true
      },
      scales: {
        yAxes: [
          {
            ticks: {
              fontColor: '#4E596D'
            }
          }
        ],
        xAxes: [
          {
            type: 'time',
            time: {
              unit: 'month',
              tooltipFormat: Constants.DATE_FORMAT_LOCALIZED
            },
            ticks: {
              fontColor: '#4E596D'
            },
            offset: true
          }
        ]
      }
    };

    this.chartData = this.getChartData();
  }

  private setupCountBasedChart(): void {
    this.chartOptions = {
      maintainAspectRatio: false,
      legend: {
        display: true,
        position: 'bottom',
        labels: {
          padding: 12,
          usePointStyle: true,
          fontSize: 14,
          fontColor: '#161C26',
          fontFamily: 'Inter'
        }
      },
      tooltips: {
        enabled: true,
        mode: 'index',
        intersect: false,
        callbacks: {
          label: function (tooltipItem, data) {
            const datasetLabel = data.datasets[tooltipItem.datasetIndex].label || '';
            const value = tooltipItem.yLabel;
            return datasetLabel + ': ' + Math.round(value) + ' parcels';
          }
        }
      },
      scales: {
        yAxes: [
          {
            ticks: {
              fontColor: '#4E596D',
              callback: function (value) {
                if (Number.isInteger(value)) {
                  return value;
                }
              }
            },
            scaleLabel: {
              display: true,
              labelString: 'Number of Parcels',
              fontColor: '#4E596D'
            }
          }
        ],
        xAxes: [
          {
            type: 'time',
            time: {
              unit: 'month',
              tooltipFormat: Constants.DATE_FORMAT_LOCALIZED
            },
            ticks: {
              fontColor: '#4E596D'
            },
            offset: true
          }
        ]
      }
    };

    this.chartData = this.getCountBasedChartData();
  }

  private getCountBasedChartData(): any {
    const categoryData = {
      range_0_25: { dates: {} },
      range_25_50: { dates: {} },
      range_50_75: { dates: {} },
      range_75_100: { dates: {} }
    };

    this.analytics.forEach((analyticData: DashboardAnalyticData) => {
      let parcel;
      if (this.$store.state.analytic.selectedProductType === ProductType.VEGETATIVE_INDEX) {
        parcel = this.$store.getters.parcelsByName[analyticData.ParcelID];
      } else {
        parcel = this.$store.state.parcels[analyticData.ParcelID];
      }

      if (parcel && analyticData.categoryCount) {
        const date = stringToMomentDate(analyticData.Published).unix();

        Object.keys(analyticData.categoryCount).forEach((category) => {
          const count = analyticData.categoryCount[category] || 0;
          if (!categoryData[category].dates[date]) {
            categoryData[category].dates[date] = 0;
          }
          categoryData[category].dates[date] += count;
        });
      }
    });

    const allDates = new Set<number>();
    Object.keys(categoryData).forEach((category) => {
      Object.keys(categoryData[category].dates).forEach((date) => {
        allDates.add(parseInt(date));
      });
    });
    const dates = Array.from(allDates).sort((a, b) => a - b);

    const categoryColors = {
      range_0_25: '#ff0000',
      range_25_50: '#ffff00',
      range_50_75: '#50c878',
      range_75_100: '#097969'
    };

    const categoryLabels = {
      range_0_25: 'Poor',
      range_25_50: 'Average',
      range_50_75: 'Good',
      range_75_100: 'Excellent'
    };

    const datasets = Object.keys(categoryData).map((category) => {
      const sortedDatesData = dates.map((date: number) => ({
        x: moment.unix(date),
        y: categoryData[category].dates[date.toString()] || 0
      }));

      return {
        datalabels: {
          labels: {
            title: null
          }
        },
        backgroundColor: categoryColors[category],
        borderColor: categoryColors[category],
        borderWidth: 2,
        pointRadius: 3,
        fill: false,
        lineTension: 0,
        label: categoryLabels[category],
        data: sortedDatesData
      };
    });

    return { datasets };
  }

  private getChartData(): any {
    const data = {};
    let countParcels;
    this.analytics.forEach((analyticData: DashboardAnalyticData) => {
      let parcel;
      if (this.$store.state.analytic.selectedProductType === ProductType.VEGETATIVE_INDEX) {
        parcel = this.$store.getters.parcelsByName[analyticData.ParcelID];
      } else {
        parcel = this.$store.state.parcels[analyticData.ParcelID];
      }
      const momentTo = stringToMomentDate(this.$store.state.analytic.fromTo.to);
      if (parcel) {
        if (
          parcel &&
          parcel.HarvestDates &&
          parcel.HarvestDates.length &&
          parcelMatchFilters(parcel, this.$store.state.mapParcelFilters, this.$store.state.analytic.fromTo) &&
          momentTo.isSameOrAfter(stringToMomentDate(parcel.HarvestDates[0]))
        ) {
          countParcels = null;
        } else {
          let fieldName = null;
          if (this.selectedType === CHART_TYPE.VARIETY) {
            fieldName = parcel.Variety;
          }
          if (this.selectedType === CHART_TYPE.CROP_SEASON && parcel.CropSeason !== undefined) {
            fieldName = parcel.CropSeason;
          }
          if (this.selectedType === CHART_TYPE.STATUS) {
            fieldName = getParcelStatus(
              parcel,
              this.$store.state.analytic.fromTo.to,
              this.$root.$i18n,
              this.$store.state.selectedCountry
            );
          }
          if (this.selectedType === CHART_TYPE.FARM_NAME && this.$store.state.selectedUnitHierarchy) {
            const farm = this.$store.state.selectedUnitHierarchy.Farms.find((farm) => farm.id === parcel.FarmID);
            if (farm) {
              fieldName = farm.Name + (farm.Code ? ` (${farm.Code})` : '');
            }
          }
          if (this.selectedType === CHART_TYPE.NUMBER_OF_CYCLES) {
            fieldName = getParcelCycle(parcel, analyticData.SurveyDate);
          }
          if (fieldName !== null) {
            let date;
            if (this.$store.state.analytic.selectedProductType === ProductType.VEGETATIVE_INDEX) {
              date = stringToMomentDate(analyticData.Published).unix();
            } else {
              date = stringToMomentDate(analyticData.SurveyDate).unix();
            }
            data[fieldName] = data[fieldName] || { totalArea: 0, dates: {} };
            data[fieldName].totalArea += parcel.Area;
            data[fieldName].dates[date] = data[fieldName].dates[date] || { totalValue1: 0, totalValue2: 0 };
            data[fieldName].dates[date].totalValue1 += analyticData.value1;
            data[fieldName].dates[date].totalValue2 += analyticData.value2;
          }
        }
      }
    });

    let labels = Object.keys(data)
      .sort((a: string, b: string) => (data[a].totalArea > data[b].totalArea ? -1 : 1))
      .slice(0, 20);

    if (this.selectedType === CHART_TYPE.NUMBER_OF_CYCLES) {
      labels = Object.keys(data).sort((a: string, b: string) => (parseInt(a) < parseInt(b) ? -1 : 1));
    }

    const dates = [
      ...new Set(labels.map((key) => Object.keys(data[key].dates).map((key) => parseInt(key))).flat())
    ].sort((a, b) => (a < b ? -1 : 1));

    const datasets = labels.map((key) => {
      const color = getColor(this.selectedType, key);
      const sortedDatesData = [];
      dates.forEach((date) => {
        const dateData = data[key].dates[date.toString()];
        if (dateData) {
          sortedDatesData.push({
            x: moment.unix(date),
            y: dateData.totalValue2 ? Math.round((dateData.totalValue1 / dateData.totalValue2) * 100 * 100) / 100 : 0
          });
        }
      });

      return {
        datalabels: {
          labels: {
            title: null
          }
        },
        backgroundColor: color,
        borderColor: color,
        borderWidth: 1,
        pointRadius: 2,
        fill: false,
        lineTension: 0,
        label: key === 'undefined' ? this.$t('undefined') : key,
        data: sortedDatesData
      };
    });

    return { datasets };
  }
}
